import React from 'react';
import { Notification } from '@interfaces/models/notification';
import clsx from 'clsx';
import VCImage from '@components/atoms/vc-images/vc-image';
import { useTranslation } from 'next-i18next';
import { NotificationTab } from '@interfaces/models/notification';
import { UseMutateFunction } from '@tanstack/react-query';
import { ReadNotificationsPayload } from '@hooks/notifications-v2/use-notification-feed-query-v2';
import { NOTIFICATION_TAB_UPDATES, NOTIFICATION_TAB_MESSAGES } from '@constants/notification';
import { calculateTimeDifference } from '@helpers/utils/notification/notification-utils';
import useUser from '@hooks/user/use-user';
import { useBucket } from '@context/bucket.context';
import ProfilePicture from '@components/molecules/profile/profile-picture';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import NotificationDetailsV2 from '../notification-details-v2/notification-details-v2';
import styles from './notification-card-v2.module.scss';

export type NotificationCardPropsV2 = {
  notification: Notification;
  selectedTab: NotificationTab;
  activeStatus?: boolean;
  onClick?: () => void;
  readNotification?: UseMutateFunction<void, void, ReadNotificationsPayload, unknown>;
  isChatCell?: boolean;
  position?: number;
};

const NotificationCardV2: React.FC<NotificationCardPropsV2> = (props): React.JSX.Element => {
  const {
    notification,
    selectedTab,
    activeStatus = false,
    onClick,
    readNotification,
    isChatCell = false,
    position,
  } = props;

  const { t } = useTranslation('common');
  const { t: tA11y } = useTranslation('a11y');

  const isUnread: boolean = notification.status !== 'read'; // older version has this condition. TODO: need to recheck for 'new' status.

  const { user } = useUser();
  const { isFeatureEnabled } = useBucket();
  const isNotificationCentreChatCellPreviewEnabled = isFeatureEnabled(
    (f) => !!f.notificationCenterEnableChatCellPreview,
    true,
  );
  const lastChatMessage = notification?.lastChatMessage;
  const isCurrentUserSender = user?.id === notification?.senderId;
  const isChatCard = notification.cardTypeV2 === 'chat';

  const messageContent = isChatCard
    ? !isNotificationCentreChatCellPreviewEnabled
      ? t('NOTIFICATION_CENTER_SCREEN_CHAT_NEW_MESSAGE')
      : isCurrentUserSender && lastChatMessage
        ? `${t('NOTIFICATION_CENTER_SCREEN_CHAT_CELL_YOU')} ${lastChatMessage}`
        : !isCurrentUserSender && lastChatMessage
          ? `${lastChatMessage}`
          : t('NOTIFICATION_CENTER_SCREEN_CHAT_NEW_MESSAGE')
    : notification?.textBody;

  return (
    <div
      className={clsx(
        styles.notificationCard,
        activeStatus && styles.notificationCard__active,
        isChatCell && styles['notificationCard__active--noRadius'],
        notification?.category === 'important' && isUnread && styles.notificationCard__important,
      )}
      id={notification?.id}
      data-cy={`notificationCard__container__${notification?.id}`}
    >
      {/* Status */}
      <div className={styles.notificationCard__statusWrapper}>
        <span
          className={clsx(
            styles.notificationCard__statusWrapper__status,
            notification.status === 'new' && styles['notificationCard__statusWrapper__status--unread'],
          )}
          data-cy={isUnread ? 'notificationCard__status__unread' : 'notificationCard__status'}
        />
      </div>
      {/* Product - Updates */}
      {selectedTab === NOTIFICATION_TAB_UPDATES && (
        <div className={styles.notificationCard__productWrapper}>
          <NotificationDetailsV2
            notification={notification}
            selectedTab={selectedTab}
            onClick={onClick}
            readNotification={readNotification}
            position={position}
          >
            <div
              className={styles.notificationCard__productWrapper__product}
              data-cy="notificationCard__product"
            >
              {notification.productPicture.path && (
                <ProfilePicture
                  disableLazyLoad
                  isUserProfile={isCurrentUserSender}
                  userId={notification.senderId}
                  picturePath={notification.productPicture.path}
                  alt={notification.productPicture.name}
                  width={48}
                  height={48}
                  className={styles.notificationCard__productWrapper__productImage}
                  data-cy="notificationCard__productImage"
                />
              )}
              {!notification.productPicture.path &&
                notification.profilePicture.path &&
                (notification?.profilePicture?.path?.includes('/assets/images/logos/') ? (
                  <img
                    src={notification?.profilePicture?.url}
                    width={48}
                    height={48}
                    className={styles.notificationCard__productWrapper__productImage}
                    alt={tA11y('A11Y.IMAGE.SELLER_AVATAR')}
                    data-cy="notificationCard__profileImage"
                  />
                ) : (
                  <ProfilePicture
                    width={48}
                    height={48}
                    isUserProfile={isCurrentUserSender}
                    picturePath={notification?.profilePicture?.path}
                    userId={notification.senderId}
                    alt={notification?.profilePicture?.name}
                    className={styles.notificationCard__productWrapper__productImage}
                    data-cy="notificationCard__productImage"
                  />
                ))}
            </div>
            {/* Description */}
            <span
              className={clsx(
                styles.notificationCard__productWrapper__detailsDescription,
                isUnread && styles['notificationCard__productWrapper__detailsDescription--unread'],
                styles[`notificationCard__productWrapper__detailsDescription--${selectedTab}`],
              )}
              data-cy={
                isUnread
                  ? 'notificationCard__productWrapper__detailsDescription__unread'
                  : 'notificationCard__productWrapper__detailsDescription'
              }
            >
              {notification.textBody}
            </span>
            {/* Date */}
            <span
              className={clsx(
                styles.notificationCard__productWrapper__detailsDate,
                isUnread && styles['notificationCard__productWrapper__detailsDate--unread'],
              )}
              data-cy={
                isUnread
                  ? 'notificationCard__productWrapper__detailsDate__unread'
                  : 'notificationCard__productWrapper__detailsDate'
              }
            >
              {calculateTimeDifference(notification?.date, t)}
            </span>
          </NotificationDetailsV2>
        </div>
      )}
      {/* Profile - Messages */}
      {selectedTab === NOTIFICATION_TAB_MESSAGES && (
        <div className={styles.notificationCard__profileWrapper}>
          <NotificationDetailsV2
            notification={notification}
            selectedTab={selectedTab}
            onClick={onClick}
            readNotification={readNotification}
            position={position}
          >
            <div
              className={styles.notificationCard__profileWrapper__profile}
              data-cy="notificationCard__profile"
            >
              <ProfilePicture
                disableLazyLoad
                isUserProfile={isCurrentUserSender}
                userId={notification.senderId}
                picturePath={
                  notification.profilePicture.path &&
                  notification.profilePicture.path !== '/' &&
                  notification.profilePicture.path
                }
                alt={tA11y('A11Y.IMAGE.SELLER_AVATAR')}
                width={48}
                height={48}
                className={styles.notificationCard__profileWrapper__profileImage}
                data-cy="notificationCard__profileImage"
              />
            </div>
            {/* Description */}
            <div className={styles.notificationCard__profileWrapper__description}>
              {isChatCard && (
                <div className={styles.notificationCard__profileWrapper__description__chat}>
                  <span
                    className={styles.notificationCard__profileWrapper__description__chat__sender}
                    data-cy="notificationCard__chat__sender"
                  >
                    {isNotificationCentreChatCellPreviewEnabled ? notification?.contactName : notification?.senderName}
                  </span>
                  <span className={styles.notificationCard__profileWrapper__description__chat__squareDot}>
                    <span
                      className={styles.notificationCard__profileWrapper__description__chat__squareDot__circle}
                    ></span>
                  </span>
                  <span
                    className={styles.notificationCard__profileWrapper__description__chat__title}
                  >{`${notification.productTitle}`}</span>
                </div>
              )}
              <div
                className={clsx(
                  styles.notificationCard__profileWrapper__detailsDescription,
                  isUnread && styles['notificationCard__profileWrapper__detailsDescription--unread'],
                  styles[`notificationCard__profileWrapper__detailsDescription--${selectedTab}`],
                  isChatCard && styles['notificationCard__profileWrapper__detailsDescription--chat'],
                )}
                data-cy={
                  isUnread
                    ? 'notificationCard__profileWrapper__detailsDescription__unread'
                    : 'notificationCard__profileWrapper__detailsDescription'
                }
              >
                {messageContent}
              </div>
            </div>
            {/* Product Image */}
            {isChatCell && (
              <ConditionalWrapper
                condition={notification.productLink.path !== '/'}
                wrap={(children) => (
                  <a
                    href={notification.productLink.path}
                    className={styles.notificationCard__product}
                    data-cy="notificationCard__product"
                  >
                    {children}
                  </a>
                )}
              >
                <VCImage
                  disableLazyLoad
                  src={notification.productPicture.path}
                  alt={notification.productPicture.name}
                  width={40}
                  height={40}
                  className={styles.notificationCard__productImage}
                  data-cy="notificationCard__productImage"
                />
              </ConditionalWrapper>
            )}
            {/* Date */}
            <span
              className={clsx(
                styles.notificationCard__profileWrapper__detailsDate,
                isUnread && styles['notificationCard__profileWrapper__detailsDate--unread'],
              )}
              data-cy={
                isUnread
                  ? 'notificationCard__profileWrapper__detailsDate__unread'
                  : 'notificationCard__profileWrapper__detailsDate'
              }
            >
              {calculateTimeDifference(notification?.date, t)}
            </span>
          </NotificationDetailsV2>
        </div>
      )}
    </div>
  );
};

export default NotificationCardV2;
